'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from '@/i18n/navigation'
import { contactSchema, type ContactInput } from '@/lib/contact'

const fieldClass =
    'w-full rounded-2xl bg-white/10 px-4 py-3 text-white placeholder:text-white/40 shadow-[inset_0_1px_2px_rgba(0,0,0,0.28)] outline-none transition-colors focus:bg-white/16 focus-visible:shadow-(--focus)'
const labelClass = 'mb-2 block text-sm font-medium text-white/80'
const errorClass = 'mt-1.5 text-sm text-[#f2b8b8]'

export default function ContactForm() {
    const t = useTranslations('contact.form')
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<ContactInput>({
        resolver: zodResolver(contactSchema),
        defaultValues: { name: '', company: '', email: '', phone: '', message: '', website: '' }
    })

    const onSubmit = handleSubmit(async (values) => {
        setStatus('idle')
        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })
            if (!res.ok) throw new Error('request_failed')
            reset()
            setStatus('success')
        } catch {
            setStatus('error')
        }
    })

    if (status === 'success') {
        return (
            <div
                role='status'
                className='flex h-full flex-col justify-center rounded-[28px] bg-white/[0.06] p-8 text-center shadow-(--shadow-md)'>
                <div className='mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#296554] text-2xl text-white'>
                    ✓
                </div>
                <h3 className='mt-5 text-xl font-semibold tracking-tight text-white'>{t('successTitle')}</h3>
                <p className='mt-3 text-white/70'>{t('successText')}</p>
            </div>
        )
    }

    return (
        <form onSubmit={onSubmit} noValidate className='space-y-5'>
            {/* Honeypot — visually hidden, not for humans */}
            <div aria-hidden='true' className='absolute left-[-9999px] h-0 w-0 overflow-hidden'>
                <label htmlFor='website'>Website</label>
                <input id='website' type='text' tabIndex={-1} autoComplete='off' {...register('website')} />
            </div>

            <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
                <div>
                    <label htmlFor='name' className={labelClass}>
                        {t('nameLabel')}
                    </label>
                    <input
                        id='name'
                        type='text'
                        autoComplete='name'
                        placeholder={t('namePlaceholder')}
                        className={fieldClass}
                        aria-invalid={errors.name ? 'true' : 'false'}
                        {...register('name')}
                    />
                    {errors.name && <p className={errorClass}>{t('errors.name')}</p>}
                </div>

                <div>
                    <label htmlFor='company' className={labelClass}>
                        {t('companyLabel')}
                    </label>
                    <input
                        id='company'
                        type='text'
                        autoComplete='organization'
                        placeholder={t('companyPlaceholder')}
                        className={fieldClass}
                        {...register('company')}
                    />
                </div>

                <div>
                    <label htmlFor='email' className={labelClass}>
                        {t('emailLabel')}
                    </label>
                    <input
                        id='email'
                        type='email'
                        autoComplete='email'
                        placeholder={t('emailPlaceholder')}
                        className={fieldClass}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        {...register('email')}
                    />
                    {errors.email && <p className={errorClass}>{t('errors.email')}</p>}
                </div>

                <div>
                    <label htmlFor='phone' className={labelClass}>
                        {t('phoneLabel')}
                    </label>
                    <input
                        id='phone'
                        type='tel'
                        autoComplete='tel'
                        placeholder={t('phonePlaceholder')}
                        className={fieldClass}
                        {...register('phone')}
                    />
                </div>
            </div>

            <div>
                <label htmlFor='message' className={labelClass}>
                    {t('messageLabel')}
                </label>
                <textarea
                    id='message'
                    rows={5}
                    placeholder={t('messagePlaceholder')}
                    className={`${fieldClass} resize-y`}
                    aria-invalid={errors.message ? 'true' : 'false'}
                    {...register('message')}
                />
                {errors.message && <p className={errorClass}>{t('errors.message')}</p>}
            </div>

            <p className='text-sm leading-relaxed text-white/55'>
                {t('privacyPrefix')}{' '}
                <Link href='/privacy' className='underline underline-offset-2 hover:text-white'>
                    {t('privacyLink')}
                </Link>
                .
            </p>

            {status === 'error' && (
                <p role='alert' className='rounded-2xl bg-[#f2b8b8]/12 px-4 py-3 text-sm text-[#f2b8b8]'>
                    {t('errorText')}
                </p>
            )}

            <button
                type='submit'
                disabled={isSubmitting}
                className='inline-flex h-13 w-full items-center justify-center rounded-2xl bg-[#296554] px-8 text-base font-semibold tracking-tight text-white transition-colors hover:bg-[#296554]/90 focus-visible:shadow-(--focus) focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto'>
                {isSubmitting ? t('sending') : t('submit')}
            </button>
        </form>
    )
}
